import React, { useEffect, useState } from 'react';
import { Button, Card, Col, DatePicker, Form, Input, Row, Select, Space } from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import { showError, showSuccess } from '@components/notification';
import {
  apiCreateSimCard,
  apiGetSimCardById,
  apiUpdateSimCard
} from '@services/ManageSim/SimServices';

const SimCardForm = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchDetail = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const response = await apiGetSimCardById(id);
        const simCard = response.data?.data;
        if (!simCard) return;
        form.setFieldsValue({
          iccid: simCard.iccid,
          imsi: simCard.imsi,
          phone_number: simCard.phone_number,
          carrier: simCard.carrier,
          sim_type: simCard.sim_type,
          plan_name: simCard.plan_name,
          activation_date: simCard.activation_date ? dayjs(simCard.activation_date) : null,
          status: simCard.status,
          technology: simCard.technology,
          notes: simCard.notes
        });
      } catch (error) {
        showError(error?.response?.data?.message || 'Không thể tải thông tin SIM.');
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [form, id]);

  const handleSubmit = async (values) => {
    try {
      setSaving(true);
      const payload = {
        ...values,
        activation_date: values.activation_date?.format('YYYY-MM-DD') || null
      };
      if (id) {
        await apiUpdateSimCard(id, payload);
      } else {
        await apiCreateSimCard(payload);
      }
      showSuccess({ message: id ? 'Cập nhật SIM thành công.' : 'Thêm mới SIM thành công.' });
      navigate('/sim-cards');
    } catch (error) {
      showError(error?.response?.data?.message || 'Không thể lưu thông tin SIM.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      title={id ? 'Cập nhật SIM' : 'Thêm mới SIM'}
      loading={loading}
      bodyStyle={{ padding: 24 }}>
      <Form
        form={form}
        layout="vertical"
        initialValues={{ status: 'STORED', sim_type: 'PHYSICAL' }}
        onFinish={handleSubmit}>
        <Row gutter={24}>
          <Col md={12} xs={24}>
            <Form.Item
              label="ICICD"
              name="iccid"
              rules={[
                { required: true, message: 'Vui lòng nhập ICICD.' },
                { max: 32, message: 'ICICD không được vượt quá 32 ký tự.' }
              ]}>
              <Input placeholder="Nhập ICICD" />
            </Form.Item>
          </Col>
          <Col md={12} xs={24}>
            <Form.Item
              label="IMSI"
              name="imsi"
              rules={[
                { required: true, message: 'Vui lòng nhập IMSI.' },
                { max: 32, message: 'IMSI không được vượt quá 32 ký tự.' }
              ]}>
              <Input placeholder="Nhập IMSI" />
            </Form.Item>
          </Col>
          <Col md={12} xs={24}>
            <Form.Item label="Số máy" name="phone_number">
              <Input placeholder="Nhập số máy" />
            </Form.Item>
          </Col>
          <Col md={12} xs={24}>
            <Form.Item
              label="Nhà mạng"
              name="carrier"
              rules={[{ required: true, message: 'Vui lòng chọn nhà mạng.' }]}>
              <Select
                showSearch
                placeholder="Chọn nhà mạng"
                options={['Viettel', 'Vinaphone', 'Mobifone', 'Vietnamobile'].map((value) => ({
                  value,
                  label: value
                }))}
              />
            </Form.Item>
          </Col>
          <Col md={12} xs={24}>
            <Form.Item
              label="Loại sim"
              name="sim_type"
              rules={[{ required: true, message: 'Vui lòng chọn loại SIM.' }]}>
              <Select
                options={[
                  { value: 'PHYSICAL', label: 'SIM vật lý' },
                  { value: 'ESIM', label: 'eSIM' }
                ]}
              />
            </Form.Item>
          </Col>
          <Col md={12} xs={24}>
            <Form.Item label="Gói cước" name="plan_name">
              <Input placeholder="Nhập tên gói cước" />
            </Form.Item>
          </Col>
          <Col md={12} xs={24}>
            <Form.Item label="Công nghệ" name="technology">
              <Input placeholder="Ví dụ: NB-IoT" />
            </Form.Item>
          </Col>
          <Col md={12} xs={24}>
            <Form.Item label="Ngày hòa mạng" name="activation_date">
              <DatePicker
                format="DD/MM/YYYY"
                placeholder="Chọn ngày hòa mạng"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col md={12} xs={24}>
            <Form.Item
              label="Trạng thái"
              name="status"
              rules={[{ required: true, message: 'Vui lòng chọn trạng thái.' }]}>
              <Select
                options={[
                  { value: 'STORED', label: 'Lưu kho' },
                  { value: 'ACTIVE', label: 'Hoạt động' },
                  { value: 'SUSPENDED', label: 'Tạm ngưng' },
                  { value: 'DAMAGED', label: 'Hư hỏng' },
                  { value: 'LOST', label: 'Thất lạc' },
                  { value: 'DEACTIVATED', label: 'Ngừng hoạt động' }
                ]}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="Ghi chú" name="notes">
              <Input.TextArea rows={3} placeholder="Nhập ghi chú" />
            </Form.Item>
          </Col>
        </Row>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/sim-cards')}>
            Quay lại
          </Button>
          <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving}>
            {id ? 'Cập nhật' : 'Thêm mới'}
          </Button>
        </Space>
      </Form>
    </Card>
  );
};

export default SimCardForm;
